import {isEqual} from "es-toolkit";
import {getIdAsKey, hydrateField, INVALIDATE} from "./mate-shared";
import {
  ABSENT,
  affectedLists,
  CachedLists,
  fieldName,
  rowFk,
  setRowField,
  targetKeys,
  type KeyInfo,
  type OrderSpec,
  type ParentRef,
  type TargetingContext,
} from "./list-targets";
import type {
  CacheEntry as Entry,
  HasManyDesc,
  ModelCache,
  ModelName,
  OptimisticRow,
  Tx,
} from "./mate-types";
import {rowMatchesQuery, type Match, type MatchContext} from "./relation-filter";

const PLACEHOLDER_PREFIX = "$opt$";

/** The `_root` relation holding each `var` a query can name, e.g. in a relation's `filter`. */
const varRootRelations: Record<string, string> = {
  currentAccountId: "account",
  currentUserId: "loggedInUser",
};

export const isPlaceholderId = (id: unknown): id is string =>
  typeof id === "string" && id.startsWith(PLACEHOLDER_PREFIX);

export const placeholderId = (mutationId: number, model: ModelName, key?: string) =>
  `${PLACEHOLDER_PREFIX}${mutationId}-${model}${key === undefined ? "" : `-${key}`}`;

export const mutationIdOfPlaceholder = (id: string) =>
  Number(id.slice(PLACEHOLDER_PREFIX.length, id.indexOf("-")));

/** Thrown by `Tx` reads of data the cache doesn't hold; the function's writes are then undone. */
class NotLoaded extends Error {}
// one instance: a function may be skipped on every rebuild, and a stack isn't needed
const NOT_LOADED = new NotLoaded("not loaded");

export type OverlayRow = {fields: Record<string, unknown>; created: boolean; deleted: boolean};
export type OverlayRows = Map<ModelName, Map<string, OverlayRow>>;
/** The loaded rows a build read, per model: their ids (`_root` for `_root`), or true for every row. */
export type OverlayReads = Map<ModelName, Set<string> | true>;

const collator = new Intl.Collator(undefined, {sensitivity: "base"});

type SortVal = number | string | bigint | boolean | null;

const toSortVal = (val: unknown): SortVal | undefined => {
  if (val === null) return null;
  if (val instanceof Date) return val.getTime();
  switch (typeof val) {
    case "number":
    case "string":
    case "bigint":
    case "boolean":
      return val;
    case "object": {
      const day = val as {year?: number; month?: number; day?: number};
      return day.year !== undefined ? day.year * 10000 + day.month! * 100 + day.day! : undefined;
    }
    default:
      return undefined;
  }
};

// Postgres puts nulls last when ascending and first when descending.
const compareSortVals = (a: SortVal, b: SortVal, desc: boolean) => {
  if (a === b) return 0;
  if (a === null) return desc ? -1 : 1;
  if (b === null) return desc ? 1 : -1;
  const getAsc = () => {
    if (typeof a === "string" && typeof b === "string") return collator.compare(a, b);
    return a < b ? -1 : a > b ? 1 : 0;
  };
  return desc ? -getAsc() : getAsc();
};

/**
 * A cached list while pending mutations change it. Edits are recorded against the list they start
 * from and applied in one pass when the list is read, so k edits to a list of n ids cost O(n + k).
 */
class ListDraft {
  private baseSet: Set<string> | null = null;
  private removed = new Set<string>();
  /** ids not in `base`, or taken out of it to go to a new position */
  private added = new Map<string, true>();
  private result: string[] | null = null;

  constructor(
    readonly base: readonly string[],
    private readonly getSortVals: ((id: string) => SortVal[] | undefined) | null,
    private readonly order: OrderSpec | null
  ) {}

  private inBase(id: string) {
    if (!this.baseSet) this.baseSet = new Set(this.base);
    return this.baseSet.has(id);
  }

  get removedCount() {
    return this.removed.size;
  }

  get size() {
    return this.base.length - this.removed.size + this.added.size;
  }

  has(id: string) {
    return this.added.has(id) || (!this.removed.has(id) && this.inBase(id));
  }

  add(id: string) {
    if (this.has(id)) return false;
    this.result = null;
    if (this.removed.has(id)) {
      this.removed.delete(id);
    } else {
      this.added.set(id, true);
    }
    return true;
  }

  remove(id: string) {
    if (!this.has(id)) return false;
    this.result = null;
    if (this.added.has(id)) {
      this.added.delete(id);
    } else {
      this.removed.add(id);
    }
    return true;
  }

  /** Takes an id out of its place so it gets sorted in again. */
  move(id: string) {
    if (!this.has(id)) return null;
    this.result = null;
    const state = {wasAdded: this.added.has(id), wasRemoved: this.removed.has(id)};
    this.added.delete(id);
    if (this.inBase(id)) this.removed.add(id);
    this.added.set(id, true);
    return () => {
      this.result = null;
      this.added.delete(id);
      if (state.wasAdded) this.added.set(id, true);
      if (!state.wasRemoved) this.removed.delete(id);
    };
  }

  materialize(): string[] {
    if (this.result) return this.result;
    const kept = this.removed.size
      ? this.base.filter((id) => !this.removed.has(id))
      : [...this.base];
    const addedIds = [...this.added.keys()];
    const {order, getSortVals} = this;
    if (!order || !getSortVals || !addedIds.length) {
      this.result = [...kept, ...addedIds];
      return this.result;
    }
    const compare = (a: SortVal[], b: SortVal[]) => {
      for (let i = 0; i < order.length; i += 1) {
        const res = compareSortVals(a[i], b[i], order[i].desc);
        if (res !== 0) return res;
      }
      return 0;
    };
    const sortable: {id: string; vals: SortVal[]}[] = [];
    const unsortable: string[] = [];
    for (const id of addedIds) {
      const vals = getSortVals(id);
      if (vals) sortable.push({id, vals});
      else unsortable.push(id);
    }
    sortable.sort((a, b) => compare(a.vals, b.vals));
    // `kept` is in server order; a new id goes after the last kept id it doesn't sort before
    const merged: string[] = [];
    let next = 0;
    for (const id of kept) {
      const vals = getSortVals(id);
      while (vals && next < sortable.length && compare(sortable[next].vals, vals) < 0) {
        merged.push(sortable[next].id);
        next += 1;
      }
      merged.push(id);
    }
    for (; next < sortable.length; next += 1) merged.push(sortable[next].id);
    this.result = [...merged, ...unsortable];
    return this.result;
  }
}

export type OverlayContext = TargetingContext & {
  modelCache: ModelCache;
  getRelationKey: (
    model: ModelName,
    relName: string,
    query: Record<string, unknown> | null | undefined
  ) => string;
  /** the id the server made for a placeholder, once its mutation resolved */
  getSavedId: (placeholder: string) => string | undefined;
};

export type LayerPart = (tx: Tx) => void;

export type CreatedRow = {
  id: string;
  model: ModelName;
  key: string | undefined;
  /** set once saved: the fields the row shows before the loaded cache has them */
  savedFields?: string[];
};

/**
 * Builds the optimistic view from scratch: the loaded cache, then each pending mutation's parts on
 * top. One builder lives for one rebuild; memos in it assume the loaded cache doesn't change
 * meanwhile.
 */
export class OverlayBuilder {
  readonly rows: OverlayRows = new Map();
  /** a build only needs redoing when one of these loaded rows changes */
  readonly reads: OverlayReads = new Map();
  private journal: (() => void)[] | null = null;
  private created: CreatedRow[] = [];
  private placeholdersSeen = new Set<string>();
  private readonly lists: CachedLists;
  private readonly tx: Tx;
  private readonly matchContext: MatchContext;

  constructor(private readonly ctx: OverlayContext) {
    this.tx = this.createTx();
    this.lists = new CachedLists(
      ctx.modelCache,
      (model, id, key) => this.read(model, id, key),
      (model, id) => this.noteRead(model, id)
    );
    this.matchContext = {
      descriptions: ctx.descriptions,
      readRow: (model, id) =>
        this.overlayRow(model, id) || this.loadedEntry(model, id) ? this.snapshot(model, id) : null,
      readVar: (name) => {
        const rootRel = varRootRelations[name];
        const val = rootRel ? this.read("_root", "_root", rootRel) : ABSENT;
        return val === ABSENT ? undefined : (val as string | null);
      },
    };
  }

  /** Runs a mutation's parts; a part that reads missing data or throws leaves nothing behind. */
  runLayer(parts: LayerPart[], mutationId: number, isResolved: boolean): CreatedRow[] {
    this.created = [];
    this.currentMutationId = mutationId;
    this.isLayerResolved = isResolved;
    for (const part of parts) {
      const journal: (() => void)[] = [];
      const createdBefore = this.created.length;
      this.journal = journal;
      try {
        part(this.tx);
      } catch (e) {
        if (!(e instanceof NotLoaded) && process.env.NODE_ENV !== "production") {
          console.error("optimistic update failed", e);
        }
        for (let i = journal.length - 1; i >= 0; i -= 1) journal[i]();
        this.created.length = createdBefore;
      }
      this.journal = null;
    }
    return this.created;
  }

  private currentMutationId = 0;
  private isLayerResolved = false;

  /** Applies the recorded list edits; call once all layers ran. */
  finish() {
    for (const modelRows of this.rows.values()) {
      for (const row of modelRows.values()) {
        for (const [field, val] of Object.entries(row.fields)) {
          if (val instanceof ListDraft) row.fields[field] = val.materialize();
        }
      }
    }
    return this.rows;
  }

  // --- reading ---

  /** Records a read of a loaded row, or of every row of the model when `id` is null. */
  private noteRead(model: ModelName, id: string | null) {
    const ids = this.reads.get(model);
    if (ids === true) return;
    if (id === null) this.reads.set(model, true);
    else if (ids) ids.add(id);
    else this.reads.set(model, new Set([id]));
  }

  private loadedEntry(model: ModelName, id: string): Entry | null | undefined {
    this.noteRead(model, id);
    const {modelCache} = this.ctx;
    if (model === "_root") return modelCache._root as unknown as Entry | undefined;
    return modelCache[model]?.[id];
  }

  private overlayRow(model: ModelName, id: string) {
    return this.rows.get(model)?.get(id);
  }

  /** The value the view shows, or ABSENT when the cache doesn't know it. */
  read(model: ModelName, id: string, field: string): unknown {
    const row = this.overlayRow(model, id);
    if (row && field in row.fields) {
      const val = row.fields[field];
      if (val instanceof ListDraft) return val.materialize();
      if (val !== INVALIDATE) return val;
    } else if (row?.created) {
      return ABSENT;
    }
    const entry = this.loadedEntry(model, id);
    if (!entry) return ABSENT;
    if (field in entry.value) return entry.value[field];
    // a parent's id is stored under the relation's name or the fk, whichever was asked for
    const fk = this.ctx.descriptions[model].belongsTo[field]?.fk;
    return fk && fk in entry.value ? entry.value[fk] : ABSENT;
  }

  /** Every field the view knows of a row; lists and unknown values are left out. */
  private snapshot(model: ModelName, id: string): Record<string, unknown> {
    const row = this.overlayRow(model, id);
    const entry = row?.created ? null : this.loadedEntry(model, id);
    const result: Record<string, unknown> = entry ? {...entry.value} : {};
    if (row) {
      const desc = this.ctx.descriptions[model];
      for (const [field, val] of Object.entries(row.fields)) {
        // keep a loaded fk from contradicting a parent written under the relation's name
        if (!(val instanceof ListDraft)) setRowField(desc, result, field, val);
      }
    }
    return result;
  }

  private isDeleted(model: ModelName, id: string) {
    return Boolean(this.overlayRow(model, id)?.deleted);
  }

  // --- writing ---

  private ensureRow(model: ModelName, id: string): OverlayRow {
    let modelRows = this.rows.get(model);
    if (!modelRows) {
      modelRows = new Map();
      this.rows.set(model, modelRows);
    }
    let row = modelRows.get(id);
    if (!row) {
      row = {fields: {}, created: false, deleted: false};
      modelRows.set(id, row);
    }
    return row;
  }

  private setField(model: ModelName, id: string, field: string, val: unknown) {
    const row = this.ensureRow(model, id);
    const prev = field in row.fields ? row.fields[field] : ABSENT;
    row.fields[field] = val;
    this.journal?.push(() => {
      if (prev === ABSENT) delete row.fields[field];
      else row.fields[field] = prev;
    });
  }

  private setFlag(model: ModelName, id: string, flag: "created" | "deleted") {
    const row = this.ensureRow(model, id);
    if (row[flag]) return;
    row[flag] = true;
    this.journal?.push(() => {
      row[flag] = false;
    });
  }

  private normalizeFields(model: ModelName, fields: Record<string, unknown>) {
    const desc = this.ctx.descriptions[model];
    const result: Record<string, unknown> = {};
    for (const [field, val] of Object.entries(fields)) {
      const name = fieldName(desc, field);
      result[name] = hydrateField(desc.fields[name]?.type, val);
    }
    return result;
  }

  // --- list upkeep ---

  private getSortValsFn(model: ModelName, order: OrderSpec | null) {
    if (!order) return null;
    const desc = this.ctx.descriptions[model];
    return (id: string) => {
      const vals: SortVal[] = [];
      for (const {field} of order) {
        const val = toSortVal(this.read(model, id, fieldName(desc, field)));
        if (val === undefined) return undefined;
        vals.push(val);
      }
      return vals;
    };
  }

  /** The draft of a parent's cached list, or null when that key isn't a list the view holds. */
  private listDraft(parent: ParentRef, parentId: string, info: KeyInfo, childModel: ModelName) {
    const row = this.overlayRow(parent.model, parentId);
    const current = row && info.key in row.fields ? row.fields[info.key] : ABSENT;
    if (current instanceof ListDraft) return current;
    const base = current === ABSENT ? this.read(parent.model, parentId, info.key) : current;
    if (!Array.isArray(base)) return null;
    const draft = new ListDraft(base, this.getSortValsFn(childModel, info.order), info.order);
    this.setField(parent.model, parentId, info.key, draft);
    return draft;
  }

  /** Keeps the index of which parents' lists hold a row in step with an edit; journaled with it. */
  private noteMembership(parent: ParentRef, rowId: string, parentId: string, delta: 1 | -1) {
    if (!this.lists.noteMembership(parent, rowId, parentId, delta)) return;
    this.journal?.push(() =>
      this.lists.noteMembership(parent, rowId, parentId, delta === 1 ? -1 : 1)
    );
  }

  /**
   * Updates every cached list of `model`'s parents that the write can change.
   * `before` is null for a created row, `after` is null for a deleted one. A row the server has
   * saved only changes lists not reloaded since: a reloaded list already tells whether it's in.
   */
  private maintainLists(
    model: ModelName,
    id: string,
    written: Set<string>,
    before: Record<string, unknown> | null,
    after: Record<string, unknown> | null,
    isSaved = false
  ) {
    const targets = affectedLists(this.ctx, this.lists, model, id, written, before, after);
    for (const target of targets) {
      const {parent, relDesc, isPlainFk} = target;
      for (const [parentId, foundBy] of target.parents) {
        // an fkAsArray list is the parent's own; which rows it takes is the server's to say
        if (foundBy === "hint") continue;
        const parentMatch = (row: Record<string, unknown> | null): Match => {
          if (row === null) return false;
          if (parent.model === "_root") return true;
          if (!isPlainFk) return "unknown";
          const fkVal = rowFk(this.ctx, row, model, parent.fk!);
          if (fkVal !== ABSENT) return fkVal === parentId;
          // the row sits in one of this parent's lists, so this is its parent
          return foundBy === "held" ? true : "unknown";
        };
        const parentEntry = isSaved ? this.loadedEntry(parent.model, parentId) : null;
        for (const info of targetKeys(this.lists, target, parentId)) {
          if (parentEntry && !parentEntry.status[info.key]) continue;
          this.maintainKey({
            parent,
            parentId,
            info,
            relDesc,
            model,
            id,
            written,
            before,
            after,
            parentMatch,
          });
        }
      }
    }
  }

  private maintainKey(opts: {
    parent: ParentRef;
    parentId: string;
    info: KeyInfo;
    relDesc: HasManyDesc;
    model: ModelName;
    id: string;
    written: Set<string>;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    parentMatch: (row: Record<string, unknown> | null) => Match;
  }) {
    const {parent, parentId, info, relDesc, model, id, written} = opts;
    const desc = this.ctx.descriptions[model];
    const matches = (row: Record<string, unknown> | null): Match => {
      if (row === null) return false;
      const byRelation = (): Match =>
        relDesc.filter ? rowMatchesQuery(relDesc.filter, desc, row, this.matchContext) : true;
      const parentRes = opts.parentMatch(row);
      if (parentRes === false) return false;
      const queryRes = rowMatchesQuery(info.matchQuery, desc, row, this.matchContext);
      if (queryRes === false) return false;
      const relationRes = byRelation();
      if (relationRes === false) return false;
      return parentRes === true && queryRes === true && relationRes === true ? true : "unknown";
    };

    switch (info.variant) {
      case "list": {
        const draft = this.listDraft(parent, parentId, info, model);
        if (!draft) {
          const current = this.read(parent.model, parentId, info.key);
          if (current === ABSENT || Array.isArray(current)) return;
          this.maintainSingleton(opts, current, matches(opts.after));
          return;
        }
        const isIn = draft.has(id);
        const nowIn = matches(opts.after);
        if (nowIn === false && isIn) {
          draft.remove(id);
          this.journal?.push(() => draft.add(id));
          this.noteMembership(parent, id, parentId, -1);
        } else if (nowIn === true && !isIn) {
          const canGrow =
            !info.isWindow ||
            (info.limit !== null &&
              draft.removedCount === 0 &&
              draft.base.length < info.limit &&
              draft.size < info.limit);
          if (!canGrow) return;
          draft.add(id);
          this.journal?.push(() => draft.remove(id));
          this.noteMembership(parent, id, parentId, 1);
        } else if (nowIn === true && isIn && this.writesOrder(model, id, info, written)) {
          const undo = draft.move(id);
          if (undo) this.journal?.push(undo);
        }
        return;
      }
      case "count":
      case "exists": {
        const current = this.read(parent.model, parentId, info.key);
        if (current === ABSENT || info.isWindow) return;
        const wasIn = opts.before === null ? false : matches(opts.before);
        const nowIn = matches(opts.after);
        if (info.variant === "exists") {
          if (nowIn === true && current !== true) {
            this.setField(parent.model, parentId, info.key, true);
          }
          return;
        }
        if (typeof current !== "number" || typeof wasIn !== "boolean") return;
        if (typeof nowIn !== "boolean" || wasIn === nowIn) return;
        this.setField(parent.model, parentId, info.key, current + (nowIn ? 1 : -1));
        return;
      }
      default:
        return;
    }
  }

  /** Whether a write gave the row a new position; an unknown new value keeps the old one. */
  private writesOrder(model: ModelName, id: string, info: KeyInfo, written: Set<string>) {
    const desc = this.ctx.descriptions[model];
    const row = this.overlayRow(model, id);
    return Boolean(
      info.order?.some(({field}) => {
        const name = fieldName(desc, field);
        return written.has(name) && row?.fields[name] !== INVALIDATE;
      })
    );
  }

  /** A relation that holds one id (`isSingleton`, `$first`). */
  private maintainSingleton(
    opts: {parent: ParentRef; parentId: string; info: KeyInfo; id: string},
    current: unknown,
    nowIn: Match
  ) {
    const {parent, parentId, info, id} = opts;
    if (nowIn === false && current === id) {
      this.setField(parent.model, parentId, info.key, null);
      this.noteMembership(parent, id, parentId, -1);
    } else if (nowIn === true && current === null) {
      // only a relation that had no match at all is known to take the row
      const loaded = this.loadedEntry(parent.model, parentId);
      if (!loaded || loaded.value[info.key] !== null) return;
      this.setField(parent.model, parentId, info.key, id);
      this.noteMembership(parent, id, parentId, 1);
    }
  }

  // --- the Tx handed to optimistic functions ---

  private row(model: ModelName, id: string): OptimisticRow {
    const desc = this.ctx.descriptions[model];
    return new Proxy({id} as OptimisticRow, {
      get: (target, prop) => {
        if (prop === "id") return id;
        if (typeof prop !== "string") return undefined;
        const field = fieldName(desc, prop);
        if (!desc.fields[field] && !desc.belongsTo[field] && !desc.hasMany[field]) return undefined;
        return this.tx.get(model, id, field);
      },
    });
  }

  private createTx(): Tx {
    const tx: Tx = {
      get: (model, id, field, ...rest) => {
        const name = fieldName(this.ctx.descriptions[model], field);
        const val = this.read(model, model === "_root" ? "_root" : id, name);
        if (val !== ABSENT) return val as any;
        if (rest.length) return rest[0];
        throw NOT_LOADED;
      },
      find: (model, id, relName, query) => {
        const key = this.ctx.getRelationKey(model, relName, query);
        const val = this.read(model, model === "_root" ? "_root" : id, key);
        if (val === ABSENT) throw NOT_LOADED;
        const childModel = this.ctx.descriptions[model].hasMany[relName].model;
        const ids = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
        return ids
          .filter((rowId) => !this.isDeleted(childModel, rowId))
          .map((rowId) => this.row(childModel, rowId));
      },
      all: (model) => {
        this.noteRead(model, null);
        const ids = new Set(
          Object.keys(this.ctx.modelCache[model] ?? {}).filter(
            (id) => this.ctx.modelCache[model]![id]
          )
        );
        for (const [id, row] of this.rows.get(model) ?? []) {
          if (row.created) ids.add(id);
        }
        return [...ids].filter((id) => !this.isDeleted(model, id)).map((id) => this.row(model, id));
      },
      create: (model, rawFields, {key} = {}) => {
        const desc = this.ctx.descriptions[model];
        const idParts = desc.idPropAsArray.map((prop) => rawFields[prop]);
        const hasOwnId = idParts.every((part) => part !== undefined && part !== null);
        const placeholder = hasOwnId ? null : placeholderId(this.currentMutationId, model, key);
        // once saved, the row shows under its own id, so a reloaded list doesn't hold it twice
        const savedId = placeholder && this.ctx.getSavedId(placeholder);
        const id = placeholder ? savedId || placeholder : getIdAsKey(idParts as string[]);
        const isNew = !hasOwnId && !savedId;
        if (isNew) {
          if (this.placeholdersSeen.has(id) && process.env.NODE_ENV !== "production") {
            console.error(`"${id}" created twice; pass a distinct \`key\` per row`);
          }
          this.placeholdersSeen.add(id);
        }
        const fields = this.normalizeFields(model, rawFields);
        // id props are read under their own name, even one that is also an fk (`cardId`)
        if (hasOwnId) desc.idPropAsArray.forEach((prop, i) => (fields[prop] = idParts[i]));
        else if (desc.idPropAsArray.length === 1) fields[desc.idPropAsArray[0]] = id;
        if (!savedId) this.setFlag(model, id, "created");
        for (const [field, val] of Object.entries(fields)) this.setField(model, id, field, val);
        if (isNew || savedId) {
          this.created.push({
            id,
            model,
            key,
            savedFields: savedId ? Object.keys(fields) : undefined,
          });
          const created = this.created;
          this.journal?.push(() => {
            const idx = created.findIndex((c) => c.id === id);
            if (idx >= 0) created.splice(idx, 1);
          });
        }
        const written = new Set(Object.keys(fields));
        // a placeholder whose mutation resolved without an id is saved too, under an id we don't know
        const isSaved = Boolean(savedId) || this.isLayerResolved;
        this.maintainLists(model, id, written, null, this.snapshot(model, id), isSaved);
        return id;
      },
      update: (model, id, rawFields) => {
        const fields = this.normalizeFields(model, rawFields);
        const names = Object.keys(fields);
        if (!names.length || this.isDeleted(model, id)) return;
        const before = this.snapshot(model, id);
        for (const field of names) this.setField(model, id, field, fields[field]);
        this.maintainLists(model, id, new Set(names), before, this.snapshot(model, id));
      },
      delete: (model, id) => {
        if (this.isDeleted(model, id)) return;
        const before = this.snapshot(model, id);
        this.setFlag(model, id, "deleted");
        this.maintainLists(model, id, new Set(Object.keys(before)), before, null);
      },
    };
    return tx;
  }
}

const sameValue = (a: unknown, b: unknown) => {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (typeof a === "object" && typeof b === "object" && a && b) {
    return isEqual(a, b);
  }
  return false;
};

const sameRow = (a: OverlayRow | undefined, b: OverlayRow | undefined) => {
  if (!a || !b) return false;
  if (a.created !== b.created || a.deleted !== b.deleted) return false;
  const aKeys = Object.keys(a.fields);
  if (aKeys.length !== Object.keys(b.fields).length) return false;
  return aKeys.every((key) => key in b.fields && sameValue(a.fields[key], b.fields[key]));
};

const isEmptyRow = (row: OverlayRow | undefined) =>
  !row || (!row.created && !row.deleted && Object.keys(row.fields).length === 0);

/** Calls `onChange` for every row whose optimistic values differ between two views. */
export const diffOverlays = (
  prev: OverlayRows,
  next: OverlayRows,
  onChange: (model: ModelName, id: string) => void
) => {
  const models = new Set([...prev.keys(), ...next.keys()]);
  for (const model of models) {
    const prevRows = prev.get(model);
    const nextRows = next.get(model);
    const ids = new Set([...(prevRows?.keys() ?? []), ...(nextRows?.keys() ?? [])]);
    for (const id of ids) {
      const a = prevRows?.get(id);
      const b = nextRows?.get(id);
      if (isEmptyRow(a) && isEmptyRow(b)) continue;
      if (!sameRow(a, b)) onChange(model, id);
    }
  }
};
